import type { AsyncThunk } from '@reduxjs/toolkit';
import type { FC, PropsWithChildren } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { CallReceivedBase } from './CallReceivedBase';

import type { HandleErrorPayload } from 'features/notification/interfaces';
import { Hotkey } from 'config/enums';

export interface CallReceivedBaseContainerProps {
  primaryButtonText?: string;
  secondaryButtonText?: string;
  primaryButtonOnClick: () => Promise<void> | void;
  secondaryButtonOnClick?: () => Promise<void> | void;
  handleErrorDispatch: AsyncThunk<void, HandleErrorPayload, object>;
  isEmergency?: boolean;
}

export const CallReceivedBaseContainer: FC<
  PropsWithChildren<CallReceivedBaseContainerProps>
> = ({
  children,
  primaryButtonText,
  primaryButtonOnClick,
  secondaryButtonText,
  secondaryButtonOnClick,
  handleErrorDispatch,
  isEmergency = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  type Handler = typeof primaryButtonOnClick | typeof secondaryButtonOnClick;

  const handleClick = useCallback(
    (handler: Handler) => async () => {
      if (!handler) {
        return;
      }
      try {
        setIsLoading(true);
        await handler();
      } catch (error) {
        setIsLoading(false);
        handleErrorDispatch(error as HandleErrorPayload);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [primaryButtonOnClick, secondaryButtonOnClick]
  );

  const primaryButton = useMemo(() => {
    return {
      localizationKey: primaryButtonText || 'takeCall',
      onClick: handleClick(primaryButtonOnClick),
      hotkey: Hotkey.TakeCall,
    };
  }, [handleClick, primaryButtonText, primaryButtonOnClick]);

  const secondaryButton = useMemo(() => {
    if (!secondaryButtonOnClick) {
      return undefined;
    }
    return {
      localizationKey: secondaryButtonText || 'declineCallAndLeaveQueue',
      onClick: handleClick(secondaryButtonOnClick),
      hotkey: Hotkey.DeclineCall,
    };
  }, [handleClick, secondaryButtonText, secondaryButtonOnClick]);

  return (
    <CallReceivedBase
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
      isLoading={isLoading}
      isEmergency={isEmergency}
    >
      {children}
    </CallReceivedBase>
  );
};
